/**
 * Admin dev utility — Clear this browser's partner attribution.
 *
 * ROUTE: /admin/tools/attribution   (admin-only)
 *
 * Why this exists
 * ───────────────
 * The customer-facing 90-day partner attribution is a purely
 * client-side construct — it lives ONLY under the localStorage key
 * `pws_partner_attribution` in the visitor's own browser. There is
 * no cookie, no sessionStorage entry, and no backend "sticky"
 * attribution record. Once a paid booking is made, the resolved
 * partner is stamped onto THAT booking row (immutable) but the
 * anonymous 90-day window never touches Mongo.
 *
 * That means "clearing" attribution during testing = removing that
 * one localStorage key in the admin's browser. This page:
 *   • Shows the current blob (for debugging / audit peace-of-mind)
 *   • Provides a one-click "Clear" button that runs the same
 *     `clearPartnerAttribution()` helper used by production code
 *   • Explains the scope so admins don't worry about affecting
 *     real customers
 *
 * Safety
 * ──────
 * This utility ONLY touches the admin's own browser. It never
 * calls the backend, never mutates any Mongo document, and never
 * changes any live customer's attribution. The live attribution
 * logic is completely unchanged.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Trash2, ShieldCheck, Info, Copy } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import {
  PARTNER_ATTRIBUTION_KEY,
  readPartnerAttribution,
  clearPartnerAttribution,
} from "@/lib/partnerAttribution";

function copyText(text) {
  try {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  } catch {
    toast.error("Copy failed — select manually");
  }
}

export default function AdminAttributionTools() {
  const [blob, setBlob] = useState(null);
  const [rawEntry, setRawEntry] = useState(null);

  const refresh = () => {
    setBlob(readPartnerAttribution());
    try {
      setRawEntry(window.localStorage.getItem(PARTNER_ATTRIBUTION_KEY));
    } catch {
      setRawEntry(null);
    }
  };

  useEffect(() => {
    document.title = "Aware Admin · Attribution Tools";
    refresh();
  }, []);

  const handleClear = () => {
    if (!blob && !rawEntry) {
      toast.info("Nothing to clear — no attribution blob in this browser.");
      return;
    }
    if (!window.confirm(
      "Clear the 90-day partner attribution from THIS browser only?\n\n"
      + "This does not affect any real customer, and it does not change any "
      + "existing paid booking's stamped attribution."
    )) return;
    clearPartnerAttribution();
    refresh();
    toast.success("Partner attribution cleared in this browser.");
  };

  const daysToExpiry = (() => {
    if (!blob?.expires_at) return null;
    const ms = Date.parse(blob.expires_at) - Date.now();
    if (!Number.isFinite(ms)) return null;
    return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
  })();

  return (
    <PortalShell>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-7">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">
            Admin · Tools
          </div>
          <h1
            data-testid="attr-tools-heading"
            className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-2 flex items-center gap-3"
          >
            <ShieldCheck className="w-8 h-8 text-cyan-600" />
            Partner attribution
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl leading-relaxed">
            Inspect and clear the 90-day partner attribution stored in{" "}
            <em>this browser</em>. Nothing on this page touches the backend
            or any other customer&rsquo;s browser.
          </p>
        </div>

        {/* Storage-location explainer */}
        <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-start gap-3">
          <Info className="w-4 h-4 mt-0.5 text-slate-500 flex-shrink-0" />
          <div className="text-[13px] text-slate-700 leading-relaxed">
            <div className="font-semibold text-slate-900 mb-1">Where is it stored?</div>
            <div>
              <code className="font-mono text-[12px] bg-white px-1.5 py-0.5 rounded border border-slate-200">
                localStorage[&ldquo;{PARTNER_ATTRIBUTION_KEY}&rdquo;]
              </code>{" "}
              — a JSON blob in your browser only. <strong>No cookie</strong>,{" "}
              <strong>no sessionStorage</strong>, <strong>no backend sticky
              record</strong> for anonymous visitors. Once a paid booking is
              made, the resolved partner is stamped onto that specific
              booking row (server-side), but the 90-day window itself lives
              entirely client-side.
            </div>
          </div>
        </div>

        {/* Current attribution */}
        <section
          data-testid="attr-tools-current"
          className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-slate-900">Current attribution</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              data-testid="attr-tools-refresh-btn"
              className="text-[12px]"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
          </div>

          {!blob && !rawEntry && (
            <div
              data-testid="attr-tools-empty"
              className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] text-emerald-900"
            >
              <ShieldCheck className="inline w-4 h-4 mr-1.5 -mt-0.5" />
              No attribution stored in this browser — bookings from this
              session will start as direct Aware visits.
            </div>
          )}

          {blob && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-[160px,1fr] gap-1 sm:gap-3 text-[13px]">
                <div className="text-slate-500 font-medium">Partner slug</div>
                <div className="font-mono" data-testid="attr-tools-slug">{blob.partner_slug}</div>
                <div className="text-slate-500 font-medium">Partner ID</div>
                <div className="font-mono text-[12px] text-slate-700">{blob.partner_id || "—"}</div>
                <div className="text-slate-500 font-medium">First click</div>
                <div>{blob.first_click_at}</div>
                <div className="text-slate-500 font-medium">Last click</div>
                <div>{blob.last_click_at}</div>
                <div className="text-slate-500 font-medium">Expires</div>
                <div>
                  {blob.expires_at}{" "}
                  {daysToExpiry != null && (
                    <span className="text-slate-400 text-[11px]">
                      · {daysToExpiry} day{daysToExpiry === 1 ? "" : "s"} left
                    </span>
                  )}
                </div>
                <div className="text-slate-500 font-medium">Source URL</div>
                <div className="text-[12px] break-all">{blob.source_url || "—"}</div>
                {blob.utm && Object.keys(blob.utm).length > 0 && (
                  <>
                    <div className="text-slate-500 font-medium">UTM</div>
                    <div className="text-[12px] font-mono">
                      {Object.entries(blob.utm).map(([k, v]) => `${k}=${v}`).join(" · ")}
                    </div>
                  </>
                )}
              </div>

              {/* Raw JSON for copy/paste */}
              <details className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12px]">
                <summary className="cursor-pointer font-semibold text-slate-700">
                  Raw JSON blob
                </summary>
                <div className="mt-2 flex items-start gap-2">
                  <pre className="flex-1 whitespace-pre-wrap font-mono text-[11px] text-slate-800 bg-white rounded p-2 border border-slate-200 overflow-auto">
{rawEntry || JSON.stringify(blob, null, 2)}
                  </pre>
                  <button
                    onClick={() => copyText(rawEntry || JSON.stringify(blob, null, 2))}
                    className="text-slate-400 hover:text-slate-800"
                    data-testid="attr-tools-copy-raw"
                    title="Copy raw JSON"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              </details>
            </div>
          )}

          {/* Clear button */}
          <div className="mt-5 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-2 justify-between">
            <p className="text-[12px] text-slate-500 max-w-md">
              Clears only your browser&rsquo;s localStorage entry. Nothing
              here changes the live customer flow, backend logic, or any
              existing paid-booking attribution stamps.
            </p>
            <Button
              onClick={handleClear}
              disabled={!blob && !rawEntry}
              data-testid="attr-tools-clear-btn"
              className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Clear my attribution
            </Button>
          </div>
        </section>

        {/* One-liner alternative */}
        <div className="mt-6 rounded-xl border border-slate-200 bg-white px-4 py-3 text-[12px] text-slate-600">
          <div className="font-semibold text-slate-900 mb-1">
            Prefer the DevTools console?
          </div>
          <code className="block font-mono text-[11px] bg-slate-900 text-emerald-300 px-3 py-2 rounded">
            localStorage.removeItem(&#39;{PARTNER_ATTRIBUTION_KEY}&#39;)
          </code>
          <p className="mt-1.5 text-slate-500">
            Runs the same operation as the Clear button above — safe to
            paste into any browser&rsquo;s DevTools console.
          </p>
        </div>
      </div>
    </PortalShell>
  );
}
